import React from "react";
import { motion } from "framer-motion";
import { MotionModal, ModalCloseButton, pressable } from "../motion";
import { cn } from "../../utils/classnames";

// "Are you sure…?" popup for customer pages. The look is the edit / cancel
// booking confirmations (DiscardEditConfirmationModal, CancelBooking…Modal):
// no icon, bold title, grey subtitle, then a grey "back" button next to the
// coloured action button.
//
// <ConfirmModal show onClose={close} onConfirm={logOut}
//   title="Log out?" cancelLabel="Cancel" confirmLabel="Log Out"
//   confirmVariant="danger" confirmIcon={<LogoutIcon />} />

const PANEL_CLASSES = "tw:px-4 tw:py-5 tw:md:px-10 tw:md:py-8";

const BUTTON_BASE_CLASSES =
  "tw:flex-1 tw:!py-3 tw:w-full tw:!rounded-[8px] tw:text-[15px] tw:leading-[22px] tw:font-hanken-grotesk tw:flex tw:items-center tw:justify-center tw:gap-2 tw:transition-colors tw:disabled:opacity-70 tw:disabled:pointer-events-none";

// The action button's colour says what kind of action it is.
const CONFIRM_VARIANT_CLASSES = {
  primary: "tw:bg-[#d4b055] tw:hover:bg-[#c2a86b] tw:text-white tw:!font-[700]", // save, proceed
  danger: "tw:bg-[#ef4136] tw:hover:bg-[#d32f2f] tw:text-white tw:!font-[700]", // log out, discard, cancel booking
};

const CANCEL_BUTTON_CLASSES =
  "tw:border tw:border-[#d0d0d0] tw:bg-white tw:hover:bg-gray-50 tw:text-[#282828] tw:font-medium";

const Spinner = () => (
  <span
    className="tw:inline-block tw:h-4 tw:w-4 tw:rounded-full tw:border-2 tw:border-current tw:border-r-transparent tw:animate-spin"
    role="status"
  >
    <span className="tw:sr-only">Loading...</span>
  </span>
);

/**
 * @param {boolean} show
 * @param {Function} onClose - ✕, Escape, backdrop click and the cancel button
 * @param {Function} onConfirm - the action button
 * @param {React.ReactNode} title
 * @param {React.ReactNode} [subtitle]
 * @param {string} cancelLabel
 * @param {string} confirmLabel
 * @param {"primary"|"danger"} [confirmVariant="primary"]
 * @param {React.ReactNode} [confirmIcon] - shown before the action label (e.g. <LogoutIcon />)
 * @param {boolean} [isLoading] - spinner on the action button, both buttons disabled
 */
const ConfirmModal = ({
  show,
  onClose,
  onConfirm,
  title,
  subtitle,
  cancelLabel,
  confirmLabel,
  confirmVariant = "primary",
  confirmIcon = null,
  isLoading = false,
}) => {
  return (
    <MotionModal show={show} onClose={onClose} maxWidthClassName="tw:max-w-[660px]" panelClassName={PANEL_CLASSES}>
      <ModalCloseButton onClick={onClose} />

      <div className="tw:!text-center tw:!mt-4 tw:!mb-6">
        <p className="tw:!text-[#282828] tw:!mb-2 tw:!text-[18px] tw:!font-[600] tw:font-hanken-grotesk tw:max-w-full">
          {title}
        </p>
        {subtitle ? (
          <p className="tw:!text-[#8D8D8D] tw:!mb-0 tw:!text-[15px] tw:!font-[400] tw:font-hanken-grotesk">{subtitle}</p>
        ) : null}
      </div>

      {/* Stacked on phones, side by side from desktop width, cancel first. */}
      <div className="tw:flex tw:flex-col tw:lg:flex-row tw:items-stretch tw:gap-2">
        <motion.button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          whileTap={pressable.tap}
          className={cn(BUTTON_BASE_CLASSES, CANCEL_BUTTON_CLASSES)}
        >
          {cancelLabel}
        </motion.button>
        <motion.button
          type="button"
          onClick={onConfirm}
          disabled={isLoading}
          whileTap={pressable.tap}
          className={cn(BUTTON_BASE_CLASSES, CONFIRM_VARIANT_CLASSES[confirmVariant] || CONFIRM_VARIANT_CLASSES.primary)}
        >
          {isLoading ? <Spinner /> : confirmIcon}
          {confirmLabel}
        </motion.button>
      </div>
    </MotionModal>
  );
};

export default ConfirmModal;
